import { formatCurrencyValue } from "shared/locale/format";
import { CurrencyCode } from "shared/types";

import { hasUsableRange } from "./utils";

interface Props {
  minimum: number | null;
  maximum: number | null;
  current: number | null;
  quoted: number;
  currentMin?: number | null;
  currentMax?: number | null;
  currencyCode: CurrencyCode;
}

const PriceLine = ({
  minimum,
  maximum,
  current,
  quoted,
  currentMin,
  currentMax,
  currencyCode,
}: Props) => {
  if (
    typeof current !== "number" ||
    typeof minimum !== "number" ||
    typeof maximum !== "number"
  ) {
    return null;
  }

  if (
    !Number.isFinite(current) ||
    !Number.isFinite(minimum) ||
    !Number.isFinite(maximum)
  ) {
    return null;
  }

  const rangeIsUsable = hasUsableRange(minimum, maximum);
  const range = rangeIsUsable ? maximum - minimum : 0;

  let quotePercentage = 0.5;
  let currentPercentage = 0.5;

  if (rangeIsUsable) {
    quotePercentage = Math.max(0, (quoted - minimum) / range);
    currentPercentage = Math.max(0, (current - minimum) / range);
  }

  return (
    <div className="flex justify-center">
      <div className="w-100 relative max-w-64">
        <div
          className="absolute h-1 bg-slate-100"
          style={{ top: "0px", left: 0, right: 0 }}
        />
        <div
          className="absolute h-4 w-0.5 bg-red-500"
          style={{ top: "-6px", left: `${quotePercentage * 100}%` }}
        />
        {rangeIsUsable &&
          currentMin &&
          currentMax &&
          currentMax !== currentMin && (
            <div
              className="relative h-1 bg-blue-200"
              style={{
                top: "0px",
                left: `${((currentMin - minimum) / range) * 100}%`,
                width: `${((currentMax - currentMin) / range) * 100}%`,
              }}
            >
              <div
                className="absolute h-2 w-2 bg-blue-500 rounded-full [clip-path:inset(0_0_0_50%)]"
                style={{ top: "-2px", left: "-5px" }}
              />
              <div
                className="absolute text-neutral-500 text-xs"
                style={{ right: "calc(100% + 5px)", top: "-6px" }}
              >
                {formatCurrencyValue(
                  currencyCode || "USD",
                  currentMin,
                  undefined,
                  0,
                  0
                )}
              </div>
              <div
                className="absolute h-2 w-2 bg-blue-500 rounded-full [clip-path:inset(0_50%_0_0)]"
                style={{ top: "-2px", right: "-5px" }}
              />
              <div
                className="absolute text-neutral-500 text-xs"
                style={{ left: "calc(100% + 5px)", top: "-6px" }}
              >
                {formatCurrencyValue(
                  currencyCode || "USD",
                  currentMax,
                  undefined,
                  0,
                  0
                )}
              </div>
            </div>
          )}
        <div
          className="absolute h-4 w-0.5 bg-red-500"
          style={{ top: "-6px", left: `${quotePercentage * 100}%` }}
        />
        <div
          className="absolute h-2 w-2 bg-blue-500 rounded-full"
          style={{
            top: "-2px",
            left: `calc(${currentPercentage * 100}% - 3px)`,
          }}
        />
        <div
          className="absolute text-xs"
          style={{
            left: `calc(${currentPercentage * 100}% - 14px)`,
            top: "12px",
          }}
        >
          {formatCurrencyValue(currencyCode || "USD", current, undefined, 0, 0)}
        </div>
      </div>
    </div>
  );
};

export default PriceLine;
